import { useQueryClient } from '@tanstack/react-query'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { formatDecimalPlaces } from '../../../helpers/functions/format-decimal-places'
import { useTranslate } from '../../../hooks/language/use-translate'
import {
  useGetUserLevelStatus,
  USER_LEVEL_STATUS_QUERY_KEY,
} from '../../../hooks/queries/level/use-get-user-level-status'
import { useGetMyProfileMini } from '../../../hooks/queries/profile/use-get-my-profile-mini'
import { useListenerMessage } from '../../../hooks/subscription/use-listener-message'
import { useSub } from '../../../hooks/subscription/use-sub'
import { useGlobalStore } from '../../../store/global-store'
import type { LiveGiftsAnimations } from '../../../types/lives/animations'

const LIVE_GIFTS_ANIMATIONS = '/LIVE-GIFTS-ANIMATIONS'

export const Level = memo(() => {
  const liveId = useGlobalStore(useShallow((state) => state.liveId))
  const queryClient = useQueryClient()

  const { user } = useGetMyProfileMini()
  const { levelStatus } = useGetUserLevelStatus()

  const [experienceSpectator, setExperienceSpectator] = useState(0)

  const { translate } = useTranslate()

  const updateLevel = useCallback(
    ({ message }: LiveGiftsAnimations) => {
      if (message?.userSentId === user?.id) {
        const returnForUser = message?.returnForUser?.flatMap(
          (returnForUser) => {
            return returnForUser?.returnForUser
          },
        )

        for (const { value } of returnForUser || []) {
          setExperienceSpectator((prevState) => prevState + value)
        }
      }
    },
    [user?.id],
  )

  useSub<LiveGiftsAnimations>({
    endpoint: `${LIVE_GIFTS_ANIMATIONS}/${liveId}`,
    skip: !liveId,
    onData: updateLevel,
  })

  const info = useMemo(() => {
    const progress =
      experienceSpectator ||
      levelStatus?.['progressBarLevelSpectatorExperience']

    const widthBar = levelStatus?.['widthBarLevelSpectatorExperience']
    // clamp: ao subir de nível o acumulado local passa do teto antigo e o
    // "quanto falta" ficava NEGATIVO até o refetch (report #45)
    const nextLevel = Math.max(
      0,
      (levelStatus?.['nextLevelSpectatorExperience'] ?? 0) - (progress ?? 0),
    )
    const pct = Math.min(100, ((progress ?? 0) / (widthBar ?? 1)) * 100)
    const quantityExperience = `${Math.max(0, pct)}%`

    return { quantityExperience, widthBar, nextLevel }
  }, [experienceSpectator, levelStatus])

  useListenerMessage(() => {
    // zera o acumulado local — senão ele sobrepõe os dados novos do servidor
    // (progress = local || servidor) e a barra ficava presa no valor velho
    setExperienceSpectator(0)
    queryClient.invalidateQueries({
      queryKey: USER_LEVEL_STATUS_QUERY_KEY,
    })
  })

  useEffect(() => {
    if (
      levelStatus &&
      experienceSpectator > (levelStatus?.['nextLevelSpectatorExperience'] ?? 0)
    ) {
      // passou do teto = subiu de nível → busca o status novo e re-baseia
      setExperienceSpectator(0)
      queryClient.invalidateQueries({ queryKey: USER_LEVEL_STATUS_QUERY_KEY })
    }
  }, [queryClient, experienceSpectator, levelStatus])

  return (
    <div className="w-screen h-screen bg-[#151515] flex">
      <div className="flex flex-row w-full items-center justify-between px-4 gap-2">
        <span className="text-xs text-white">
          {translate('modal-gifts-level')}{' '}
          {formatDecimalPlaces(levelStatus?.levelSpectatorId ?? 0)}
        </span>

        <div className="flex flex-1 flex-col items-center">
          <div className="flex w-full h-1 rounded-sm border border-gray-300 overflow-hidden">
            <div
              className="h-full bg-[#4568DC]"
              style={{ width: info.quantityExperience }}
            />
          </div>

          <span className="text-[8px] leading-none mt-1 text-white">
            {formatDecimalPlaces(info.nextLevel)} XP
          </span>
        </div>
      </div>
    </div>
  )
})
